import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  computeTooltipPlacement,
  type TooltipMode,
  type TooltipPlacement,
} from '../lib/tooltipPlacement';

/**
 * Single tooltip layer for the entire application.
 *
 * There used to be 180 native `title=""` attributes. The browser's `title` has three
 * problems that styling cannot fix:
 *
 * 1. **Takes ~1.5 seconds** to appear and disappears after 5, with no control.
 * 2. **Does not exist on touch.** On a phone or tablet, an icon with `title` has no way
 *    to explain itself: half of the product's actions are icon buttons.
 * 3. **Renders however the OS wants** — font, color, and position are outside the product.
 *
 * The usual alternative is a wrapper component around each trigger, which forces rewriting
 * 180 places and fighting `overflow: hidden` on every table. Here it works the other way:
 * **a single listener on the document**. Any element with `data-tooltip` inherits it, no
 * matter where it comes from, and the panel renders in `fixed` position.
 *
 * z-index: the layer used to share `z-[100]` with the sidebar *and* was mounted *before*
 * `AppShell`, so a tooltip for a nav item was painted underneath the aside. It sits at
 * 400 — above the nav (100), mobile drawer (200) and header menus (250), below modals
 * (1000). Sidebar triggers also open to the **right** of the nav so the text is never
 * hidden behind it.
 *
 * Accessibility: the control name still lives in `aria-label`; the tooltip is visual
 * reinforcement and is linked with `aria-describedby` while open. Escape closes it, and on
 * touch it opens on tap and closes on tap outside.
 */

const ID_PANEL = 'capa-tooltip';
const RETARDO_MS = 120;

interface Posicion extends TooltipPlacement {
  texto: string;
}

function cajaDe(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
}

function esDelSidebar(el: HTMLElement) {
  return Boolean(el.closest('[data-app-sidebar]'));
}

function colocar(el: HTMLElement, panel: HTMLDivElement | null): TooltipPlacement {
  return computeTooltipPlacement(
    cajaDe(el),
    { width: panel?.offsetWidth ?? 160, height: panel?.offsetHeight ?? 40 },
    { width: window.innerWidth, height: window.innerHeight },
    { preferRight: esDelSidebar(el) },
  );
}

function transformDe(mode: TooltipMode) {
  if (mode === 'right') return 'translate(0, -50%)';
  if (mode === 'above') return 'translate(-50%, -100%)';
  return 'translate(-50%, 0)';
}

export const TooltipLayer: React.FC = () => {
  const [posicion, setPosicion] = useState<Posicion | null>(null);
  const temporizador = useRef<number | null>(null);
  const disparador = useRef<HTMLElement | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  const cerrar = useCallback(() => {
    if (temporizador.current) {
      window.clearTimeout(temporizador.current);
      temporizador.current = null;
    }
    if (disparador.current) {
      disparador.current.removeAttribute('aria-describedby');
      disparador.current = null;
    }
    setPosicion(null);
  }, []);

  const abrir = useCallback((el: HTMLElement, inmediato: boolean) => {
    const texto = el.getAttribute('data-tooltip');
    if (!texto) return;

    const mostrar = () => {
      disparador.current = el;
      el.setAttribute('aria-describedby', ID_PANEL);
      setPosicion({ texto, ...colocar(el, panel.current) });
    };

    if (temporizador.current) window.clearTimeout(temporizador.current);
    if (inmediato) mostrar();
    else temporizador.current = window.setTimeout(mostrar, RETARDO_MS);
  }, []);

  useEffect(() => {
    const buscar = (destino: EventTarget | null): HTMLElement | null => {
      if (!(destino instanceof Element)) return null;
      return destino.closest<HTMLElement>('[data-tooltip]');
    };

    const alEntrar = (e: MouseEvent) => {
      const el = buscar(e.target);
      if (el) abrir(el, false);
      else if (disparador.current) cerrar();
    };
    // Focus arrives via keyboard and also on touch tap: in both cases the tooltip should
    // appear without waiting.
    const alEnfocar = (e: FocusEvent) => {
      const el = buscar(e.target);
      if (el) abrir(el, true);
    };
    const alSalir = (e: Event) => {
      const el = buscar(e.target);
      if (el && el === disparador.current) cerrar();
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    // A fixed-position panel floats stale if the page scrolls.
    const alDesplazar = () => { if (disparador.current) cerrar(); };
    const alTocarFuera = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (!buscar(e.target)) cerrar();
    };
    // A trigger that opens a menu (bell, view as…) explains itself with the menu: on a phone the
    // tap-tooltip was painted over the panel it had just opened (T-21).
    const alPulsar = (e: MouseEvent) => {
      const el = buscar(e.target);
      if (el && el.matches('[aria-haspopup],[aria-expanded]')) cerrar();
    };

    document.addEventListener('mouseover', alEntrar);
    document.addEventListener('mouseout', alSalir);
    document.addEventListener('focusin', alEnfocar);
    document.addEventListener('focusout', alSalir);
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('pointerdown', alTocarFuera);
    document.addEventListener('click', alPulsar);
    window.addEventListener('scroll', alDesplazar, true);
    window.addEventListener('resize', alDesplazar);

    return () => {
      document.removeEventListener('mouseover', alEntrar);
      document.removeEventListener('mouseout', alSalir);
      document.removeEventListener('focusin', alEnfocar);
      document.removeEventListener('focusout', alSalir);
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('pointerdown', alTocarFuera);
      document.removeEventListener('click', alPulsar);
      window.removeEventListener('scroll', alDesplazar, true);
      window.removeEventListener('resize', alDesplazar);
    };
  }, [abrir, cerrar]);

  // Recalculate with the painted size: until then we don't know if a long description
  // overflows the viewport or still sits under the sidebar.
  useEffect(() => {
    if (!posicion || !panel.current || !disparador.current) return;
    const placed = colocar(disparador.current, panel.current);
    if (
      Math.abs(placed.x - posicion.x) > 0.5
      || Math.abs(placed.y - posicion.y) > 0.5
      || placed.mode !== posicion.mode
    ) {
      setPosicion((p) => (p ? { ...p, ...placed } : p));
    }
  }, [posicion]);

  if (!posicion) return null;

  return (
    <div
      ref={panel}
      id={ID_PANEL}
      role="tooltip"
      className="pointer-events-none fixed z-[400] w-max max-w-[min(20rem,calc(100vw-1rem))] rounded-control bg-ink px-2.5 py-1.5 text-[0.8125rem] leading-snug text-surface shadow-overlay"
      style={{
        left: posicion.x,
        top: posicion.y,
        transform: transformDe(posicion.mode),
      }}
    >
      {posicion.texto}
    </div>
  );
};

export default TooltipLayer;
